// Audio engine using Web Audio API for synthesized sax-like tones

const NOTE_FREQUENCIES = {};
const NOTE_NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B'];

// Build frequency table for all notes
for (let octave = 0; octave <= 8; octave++) {
  NOTE_NAMES.forEach((name, semitone) => {
    const midi = (octave + 1) * 12 + semitone;
    const freq = 440 * Math.pow(2, (midi - 69) / 12);
    NOTE_FREQUENCIES[`${name}${octave}`] = freq;
  });
}

export function createAudioEngine() {
  let audioCtx = null;
  let currentOsc = null;
  let currentGain = null;

  function ensureContext() {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }
    return audioCtx;
  }

  function playNote(noteName, durationMs) {
    const ctx = ensureContext();
    const freq = NOTE_FREQUENCIES[noteName];
    if (!freq) return;

    // Stop previous note
    stop();

    // Primary oscillator — sawtooth contains all harmonics (sax-like)
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = freq;

    // Slight vibrato for realism
    const vibrato = ctx.createOscillator();
    vibrato.type = 'sine';
    vibrato.frequency.value = 5; // 5 Hz vibrato rate
    const vibratoGain = ctx.createGain();
    vibratoGain.gain.value = freq * 0.005; // very subtle pitch wobble
    vibrato.connect(vibratoGain);
    vibratoGain.connect(osc.frequency);

    // Lowpass filter to tame harsh high harmonics
    const lpFilter = ctx.createBiquadFilter();
    lpFilter.type = 'lowpass';
    lpFilter.frequency.value = Math.min(freq * 4, 8000);
    lpFilter.Q.value = 1;

    // Formant-like bandpass for sax body
    const bpFilter = ctx.createBiquadFilter();
    bpFilter.type = 'peaking';
    bpFilter.frequency.value = Math.max(freq * 2.5, 800);
    bpFilter.Q.value = 1.5;
    bpFilter.gain.value = 4;

    // Main gain with ADSR envelope
    const gainNode = ctx.createGain();
    const now = ctx.currentTime;
    const durSec = durationMs / 1000;
    const attack = 0.03;
    const decay = 0.06;
    const sustain = 0.25;
    const release = Math.min(0.12, durSec * 0.2);

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.35, now + attack);
    gainNode.gain.linearRampToValueAtTime(sustain, now + attack + decay);
    gainNode.gain.setValueAtTime(sustain, now + Math.max(durSec - release, attack + decay));
    gainNode.gain.linearRampToValueAtTime(0, now + durSec);

    // Connect: osc → lowpass → bandpass → gain → output
    osc.connect(lpFilter);
    lpFilter.connect(bpFilter);
    bpFilter.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc.start(now);
    vibrato.start(now);
    osc.stop(now + durSec + 0.01);
    vibrato.stop(now + durSec + 0.01);

    currentOsc = osc;
    currentGain = gainNode;
  }

  function stop() {
    if (currentGain) {
      try {
        currentGain.gain.cancelScheduledValues(0);
        currentGain.gain.setValueAtTime(0, 0);
      } catch (e) { /* ignore */ }
    }
    currentOsc = null;
    currentGain = null;
  }

  function dispose() {
    stop();
    if (audioCtx) {
      audioCtx.close();
      audioCtx = null;
    }
  }

  return { playNote, stop, dispose };
}
